// Small geometry helpers, shared by server and browser.
export interface LatLon {
  lat: number;
  lon: number;
}

const R = 6371000;
const rad = (d: number) => (d * Math.PI) / 180;

export function haversine(a: LatLon, b: LatLon): number {
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Geometric median via Weiszfeld's algorithm on a local equirectangular projection.
// Minimises the sum of distances, which is more robust than the centroid when one person lives far out.
export function geometricMedian(points: LatLon[], weights: number[] = points.map(() => 1)): LatLon | null {
  if (points.length === 0) return null;
  if (points.length === 1) return { lat: points[0].lat, lon: points[0].lon };
  const lat0 = points.reduce((s, p) => s + p.lat, 0) / points.length;
  const kx = Math.cos(rad(lat0));
  const xy = points.map((p) => [p.lon * kx, p.lat] as const);
  const W = weights.reduce((s, w) => s + w, 0);
  let x = xy.reduce((s, p, i) => s + p[0] * weights[i], 0) / W;
  let y = xy.reduce((s, p, i) => s + p[1] * weights[i], 0) / W;
  for (let iter = 0; iter < 200; iter++) {
    let nx = 0;
    let ny = 0;
    let den = 0;
    for (let i = 0; i < xy.length; i++) {
      const d = Math.hypot(xy[i][0] - x, xy[i][1] - y) || 1e-9;
      const w = weights[i] / d;
      nx += xy[i][0] * w;
      ny += xy[i][1] * w;
      den += w;
    }
    nx /= den;
    ny /= den;
    const moved = Math.hypot(nx - x, ny - y);
    x = nx;
    y = ny;
    if (moved < 1e-8) break;
  }
  return { lat: y, lon: x / kx };
}

export const round = (v: number, digits = 3) => Math.round(v * 10 ** digits) / 10 ** digits;
